<template>
  <div class="RendererHtml">
    <pre v-if="viewMode === 'code'" ref="codeEl" class="RendererHtml__code">
      {{ typedContent }}
    </pre>
    <iframe
      v-else
      ref="frame"
      :srcdoc="frameSrcdoc"
      :sandbox="GENERATED_HTML_SANDBOX"
      class="RendererHtml__frame"
      :data-resizing="isResizing"
    />
    <Transition name="RendererHtml-building">
      <div v-if="isBuildingUnstyled" class="RendererHtml__building">
        <ChatAssistantLiveStep label="Building page" />
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue'

import { useThrottleFn } from '@vueuse/core'

import { useDraftTypewriter } from '@/composables/useDraftTypewriter'
import { GENERATED_HTML_SANDBOX } from '@/constants/generatedHtmlSandbox'
import type { HtmlPreviewViewMode } from '@/store/preview'
import {
  DRAFT_HTML_MESSAGE_TYPE,
  DRAFT_READY_MESSAGE_TYPE,
  DRAFT_SCROLL_MESSAGE_TYPE,
  isDraftPageStyled,
  PREVIEW_NODE_ATTRIBUTE,
  previewDraftBridgeScriptSource,
  previewDraftScrollScriptSource,
} from '@/utils/previewDraftFrame'
import { previewScrollbarStyleSource } from '@/utils/previewScrollbarStyle'
import { publishedHtmlLinkScriptSource } from '@/utils/publishedHtmlLinks'
import { sandboxedStorageShimScriptSource } from '@/utils/sandboxedStorageShim'
import ChatAssistantLiveStep from '@/views/components/chat/ChatAssistantLiveStep.vue'

export interface Props {
  /** The page's HTML source, rendered in the frame or shown as code. */
  content: string
  /** Split-pane drag in progress: the frame stops taking pointer events. */
  isResizing: boolean
  /** Rendered page, or its source as code. */
  viewMode?: HtmlPreviewViewMode
  /** A page the HTML agent is still writing: later drafts update the page in place. */
  isLiveDraft?: boolean
}

const props = defineProps<Props>()

/** The content as shown: a live draft is typed out between drafts, anything else as is. */
const { text: typedContent } = useDraftTypewriter(
  () => props.content,
  () => !!props.isLiveDraft,
)

// #region Code view
const codeEl = useTemplateRef<HTMLElement>('codeEl')

watch(typedContent, async () => {
  if (props.viewMode !== 'code') return
  await nextTick()
  const el = codeEl.value
  if (!el) return
  el.scrollTop = el.scrollHeight
})
// #endregion

// #region Preview document
function ensureHead(doc: Document): HTMLHeadElement {
  let head = doc.querySelector('head')
  if (head) return head
  head = doc.createElement('head')
  const htmlEl = doc.documentElement
  const insertBefore = htmlEl.querySelector('body')
  if (insertBefore) {
    htmlEl.insertBefore(head, insertBefore)
  } else {
    htmlEl.insertBefore(head, htmlEl.firstChild)
  }
  return head
}

interface IPreviewDocumentOptions {
  /** Drop the page's scripts (the finished page runs them) and add the draft bridge. */
  isLiveDraft: boolean
  /** Restore this scroll and report later ones; `null` leaves the scroll alone. */
  scrollY: number | null
}

/** The page as the preview frame shows it. Drafts are built the same way to be posted. */
function buildPreviewDocument(
  content: string,
  { isLiveDraft, scrollY }: IPreviewDocumentOptions,
): Document {
  const parser = new DOMParser()
  const doc = parser.parseFromString(content, 'text/html')

  const cspMetaTags = doc.querySelectorAll(
    'meta[http-equiv="Content-Security-Policy" i]',
  )

  cspMetaTags.forEach((meta) => meta.remove())

  if (isLiveDraft) {
    doc.querySelectorAll('script').forEach((script) => script.remove())
  }

  const head = ensureHead(doc)
  const prepend = (tagName: 'style' | 'script', source: string) => {
    const el = doc.createElement(tagName)
    el.textContent = source
    // Marked, so the draft bridge leaves it in place when it patches the head.
    el.setAttribute(PREVIEW_NODE_ATTRIBUTE, '')
    head.insertBefore(el, head.firstChild)
  }

  // Ahead of the page's own styles, so a page that styles its scrollbar still wins.
  prepend('style', previewScrollbarStyleSource())
  prepend('script', publishedHtmlLinkScriptSource())
  if (isLiveDraft) prepend('script', previewDraftBridgeScriptSource())
  if (scrollY !== null)
    prepend('script', previewDraftScrollScriptSource(scrollY))
  // Prepended last so it lands first: the shim must exist before any page script runs.
  prepend('script', sandboxedStorageShimScriptSource())

  return doc
}
// #endregion

// #region Frame
/** The page being written is patched at most this often: the frame parses the whole page. */
const DRAFT_PATCH_INTERVAL_MS = 150

const frame = useTemplateRef<HTMLIFrameElement>('frame')
const frameSrcdoc = ref('')
/** Where the reader is in the draft. Plain, not reactive: it must not rebuild the page. */
let draftScrollY = 0
/** The frame holds a draft page with the bridge, so later drafts are posted, not reloaded. */
let hasDraftFrame = false
/** The bridge in the frame has said it takes drafts. */
let isDraftFrameReady = false
/** The draft frame has been sent a draft with the page's own styles written. */
const isDraftStyled = ref(false)

/** A page being built whose styles are still being written: "Building page" covers it. */
const isBuildingUnstyled = computed(
  () =>
    !!props.isLiveDraft && props.viewMode !== 'code' && !isDraftStyled.value,
)

/** Sends the draft as typed so far, raw: the bridge cleans, parses and patches it in. */
const postDraft = () => {
  if (!props.isLiveDraft || !isDraftFrameReady) return
  const html = typedContent.value
  frame.value?.contentWindow?.postMessage(
    { type: DRAFT_HTML_MESSAGE_TYPE, html },
    '*',
  )
  if (!isDraftStyled.value && isDraftPageStyled(html)) {
    isDraftStyled.value = true
  }
}
// Trailing, so the last of a burst always lands.
const postDraftThrottled = useThrottleFn(
  postDraft,
  DRAFT_PATCH_INTERVAL_MS,
  true,
)

watch(
  () => [typedContent.value, props.isLiveDraft] as const,
  ([content, isLiveDraft], previous) => {
    if (!isLiveDraft) {
      // The finished page loads fresh (its scripts must run), where the draft was scrolled to.
      const wasLiveDraft = previous?.[1] === true
      frameSrcdoc.value = buildPreviewDocument(content, {
        isLiveDraft: false,
        scrollY: wasLiveDraft ? draftScrollY : null,
      }).documentElement.outerHTML
      hasDraftFrame = false
      isDraftFrameReady = false
      isDraftStyled.value = false
      draftScrollY = 0
      return
    }

    if (!hasDraftFrame) {
      frameSrcdoc.value = buildPreviewDocument(content, {
        isLiveDraft: true,
        scrollY: draftScrollY,
      }).documentElement.outerHTML
      hasDraftFrame = true
      isDraftFrameReady = false
      isDraftStyled.value = false
      return
    }
    // A frame still loading takes the latest draft once ready.
    void postDraftThrottled()
  },
  { immediate: true },
)

// The code view unmounts the frame; back in the rendered view it loads again.
watch(
  () => props.viewMode,
  (viewMode) => {
    if (viewMode !== 'code') return
    isDraftFrameReady = false
    isDraftStyled.value = false
  },
)

const onFrameMessage = (event: MessageEvent) => {
  if (event.source !== frame.value?.contentWindow) return
  const data = event.data as { type?: unknown; y?: unknown } | null
  if (data?.type === DRAFT_SCROLL_MESSAGE_TYPE && typeof data.y === 'number') {
    draftScrollY = data.y
  } else if (data?.type === DRAFT_READY_MESSAGE_TYPE && hasDraftFrame) {
    isDraftFrameReady = true
    // The latest draft, which may be newer than the one the frame loaded.
    postDraft()
  }
}

onMounted(() => window.addEventListener('message', onFrameMessage))
onBeforeUnmount(() => window.removeEventListener('message', onFrameMessage))
// #endregion
</script>

<style lang="scss" scoped>
.RendererHtml {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
}

.RendererHtml__building {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary);
}

.RendererHtml-building-leave-active {
  transition: opacity 0.2s ease;
}

.RendererHtml-building-leave-to {
  opacity: 0;
}

.RendererHtml__frame {
  flex: 1;
  width: 100%;
  min-height: 0;
  border: none;
  background: var(--bg-primary);

  &[data-resizing='true'] {
    pointer-events: none;
  }
}

.RendererHtml__code {
  flex: 1;
  width: 100%;
  min-height: 0;
  margin: 0;
  @include customScrollbarInset(1rem);
  border: none;
  background: var(--bg-tertiary, #fafafa);
  color: var(--text-primary);
  @include font-code-small-regular;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
